/*
 * Copyright (c) 2017-2026 Brighter AI Technologies GmbH. All rights reserved.
 * Unauthorized use, copying, reproduction, or distribution of this file, via any medium,
 * is strictly prohibited.
 */

import { createMuiTheme } from "@material-ui/core/styles";

const invisible = "rgba(0, 0, 0, 0)";

const theme = createMuiTheme({
  palette: {
    type: "light",
    primary: {
      main: "#82F0D7",
      contrastText: "#23285A",
    },
    secondary: {
      main: "#23285A",
      contrastText: "#7D87FF",
    },
    error: {
      main: "#FF00B1",
    },
    warning: {
      main: "#FF00B1",
    },
    info: {
      main: "#7D87FF",
      contrastText: "#D9FBED",
    },
    success: {
      main: "#87F0D7",
      contrastText: "#23285A",
    },
    invisible: invisible,
    text: {
      secondary: "#82F0D7",
      hint: "rgba(156,156,156,0.75)",
      primary: "#23285A",
      lightGray: "#EFEFEF",
    },
  },
  typography: {
    fontFamily: "Telegraf-Regular",
    h1: {
      fontSize: 60,
      lineHeight: 1,
    },
    h2: {
      fontSize: 36,
    },
    h3: {
      fontSize: 24,
    },
    h4: {
      fontSize: 16,
    },
    h5: {
      fontSize: 12,
    },
  },
  spacing: 5,
  shape: {
    borderRadius: 0,
  },
  shadows: [
    "none",
    "0px 2px 10px -1px rgba(0, 0, 0, 0.1)",
    "0px 2px 10px -1px rgba(0, 0, 0, 0.1)",
    "0px 2px 10px -1px rgba(0, 0, 0, 0.1)",
    "0px 2px 10px -1px rgba(0, 0, 0, 0.1)",
    "0px 2px 10px -1px rgba(0, 0, 0, 0.1)",
    "0px 2px 10px -1px rgba(0, 0, 0, 0.1)",
    "0px 2px 10px -1px rgba(0, 0, 0, 0.1)",
    "0px 2px 10px -1px rgba(0, 0, 0, 0.1)",
    "0px 2px 10px -1px rgba(0, 0, 0, 0.1)",
    "0px 2px 10px -1px rgba(0, 0, 0, 0.1)",
    "0px 2px 10px -1px rgba(0, 0, 0, 0.1)",
    "0px 2px 10px -1px rgba(0, 0, 0, 0.1)",
    "0px 2px 10px -1px rgba(0, 0, 0, 0.1)",
    "0px 2px 10px -1px rgba(0, 0, 0, 0.1)",
    "0px 2px 10px -1px rgba(0, 0, 0, 0.1)",
    "0px 2px 10px -1px rgba(0, 0, 0, 0.1)",
    "0px 2px 10px -1px rgba(0, 0, 0, 0.1)",
    "0px 2px 10px -1px rgba(0, 0, 0, 0.1)",
    "0px 2px 10px -1px rgba(0, 0, 0, 0.1)",
    "0px 2px 10px -1px rgba(0, 0, 0, 0.1)",
    "0px 2px 10px -1px rgba(0, 0, 0, 0.1)",
    "0px 2px 10px -1px rgba(0, 0, 0, 0.1)",
    "0px 2px 10px -1px rgba(0, 0, 0, 0.1)",
    "0px 2px 10px -1px rgba(0, 0, 0, 0.1)",
  ],
  props: {
    MuiButton: {
      disableElevation: true,
    },
    MuiTextField: {
      margin: "dense",
    },
    RaMuiButton: {
      disableElevation: true,
    },
  },
  sidebar: {
    width: 230,
    closedWidth: 0,
  },
});

/* Set up breakpoints and overrides for typography */

theme.typography.h1 = {
  ...theme.typography.h1,
  [theme.breakpoints.down("md")]: {
    fontSize: 36,
    lineHeight: 1.111111,
  },
};

theme.typography.h2 = {
  ...theme.typography.h2,
  [theme.breakpoints.down("md")]: {
    fontSize: 24,
  },
};

theme.typography.body1 = {
  ...theme.typography.h4,
  color: theme.palette.text.primary,
};

theme.typography.body2 = {
  ...theme.typography.h4,
  color: theme.palette.text.hint,
};

theme.typography.subtitle1.fontSize = theme.typography.h3.fontSize;
theme.typography.subtitle2.fontSize = theme.typography.h4.fontSize;
theme.typography.button.fontSize = theme.typography.h4.fontSize;
theme.typography.caption.fontSize = theme.typography.h4.fontSize;
theme.typography.overline.fontSize = theme.typography.h4.fontSize;

/* Set up overrides */

theme.overrides.MuiButton = {
  contained: {
    backgroundColor: theme.palette.primary.main,
    "&:hover": {
      border: "1px solid " + theme.palette.secondary.main,
      backgroundColor: theme.palette.primary.main,
    },
  },
  outlined: {
    padding: "6px 16px",
    backgroundColor: invisible,
    border: "1px solid " + theme.palette.secondary.main,
    "&:hover": {
      border: "1px solid " + theme.palette.info.main,
      backgroundColor: invisible,
    },
  },
  text: {
    color: theme.palette.text.primary,
  },
  root: {
    border: "1px solid " + invisible,
  },
  containedPrimary: {
    "&:hover": {
      backgroundColor: null,
    },
  },
  containedSecondary: {
    "&:hover": {
      backgroundColor: null,
    },
  },
  outlinedPrimary: {
    "&:hover": {
      backgroundColor: null,
    },
  },
  outlinedSecondary: {
    "&:hover": {
      backgroundColor: null,
    },
  },
};

theme.overrides.RaMenuItemLink = {
  root: {
    color: theme.palette.text.primary,
    borderLeft: "6px solid " + invisible,
    fontSize: theme.typography.h4.fontSize,
  },
  icon: {
    marginRight: theme.spacing(3),
  },
  active: {
    borderLeft: "6px solid " + theme.palette.primary.main,
  },
};

// mainly to override loading button icons
theme.overrides.MuiCircularProgress = {
  colorPrimary: {
    color: theme.palette.secondary.main,
  },
};

/* Card overrides */

theme.overrides.MuiCardHeader = {
  title: theme.typography.h2,
};

/* Text and Input overrides */

theme.overrides.MuiInputLabel = {
  root: {
    fontSize: theme.typography.h4.fontSize,
    color: theme.palette.text.hint,
    "&$focused": {
      color: theme.palette.text.primary,
    },
  },
};

theme.overrides.MuiInputBase = {
  root: {
    fontSize: theme.typography.h4.fontSize,
    "&$focused": {
      borderColor: theme.palette.primary.main,
    },
  },
};

theme.overrides.RaAutocompleteInput = {
  suggestionsContainer: {
    "& *": {
      fontSize: theme.typography.h4.fontSize,
    },
  },
};

/* Link element */

theme.overrides.MuiLink = {
  root: {
    color: theme.palette.secondary.contrastText,
  },
};

theme.overrides.MuiBackdrop = {
  root: {
    backgroundColor: theme.palette.primary.contrastText,
    opacity: "0.5 !important",
  },
};

export default theme;
